import { useAuth } from '@clerk/clerk-react';

const API_URL = import.meta.env.VITE_API_URL || '';

export function useApi() {
  const { getToken } = useAuth();

  const fetchWithAuth = async (endpoint, options = {}) => {
    const token = await getToken();
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || error.message || 'Request failed');
    }

    return response.json();
  };

  return {
    // Products
    getManufacturers: (category) => 
      fetchWithAuth(`/products/manufacturers${category ? `?category=${category}` : ''}`),
    
    getProducts: (filters = {}) => {
      const params = new URLSearchParams(filters).toString();
      return fetchWithAuth(`/products${params ? `?${params}` : ''}`);
    },
    
    getLabourRates: (category) => 
      fetchWithAuth(`/products/labour/rates${category ? `?category=${category}` : ''}`),
    
    submitProductRequest: (data) => 
      fetchWithAuth('/products/requests', { method: 'POST', body: JSON.stringify(data) }),

    // Projects
    getProjects: () => fetchWithAuth('/projects'),
    
    getProject: (id) => fetchWithAuth(`/projects/${id}`),
    
    createProject: (data) => 
      fetchWithAuth('/projects', { method: 'POST', body: JSON.stringify(data) }),
    
    updateProject: (id, data) => 
      fetchWithAuth(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    
    deleteProject: (id) => 
      fetchWithAuth(`/projects/${id}`, { method: 'DELETE' }),
    
    cloneProject: (id, name) => 
      fetchWithAuth(`/projects/${id}/clone`, { method: 'POST', body: JSON.stringify({ name }) }),

    // User
    getCurrentUser: () => fetchWithAuth('/auth/me'),
    
    updateProfile: (data) => 
      fetchWithAuth('/auth/me', { method: 'PATCH', body: JSON.stringify(data) }),

    // Feature requests
    submitFeatureRequest: (data) =>
      fetchWithAuth('/products/feature-request', { method: 'POST', body: JSON.stringify(data) }),

    // Admin
    checkAdminStatus: () => fetchWithAuth('/admin/status'),
    
    adminGetProducts: (filters = {}) => {
      const params = new URLSearchParams(filters).toString();
      return fetchWithAuth(`/admin/products${params ? `?${params}` : ''}`);
    },
    
    adminGetProduct: (id) => fetchWithAuth(`/admin/products/${id}`),
    
    adminUpdateProduct: (id, data) => 
      fetchWithAuth(`/admin/products/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    
    adminCreateProduct: (data) => 
      fetchWithAuth('/admin/products', { method: 'POST', body: JSON.stringify(data) }),
    
    adminDeleteProduct: (id) => 
      fetchWithAuth(`/admin/products/${id}`, { method: 'DELETE' }),
    
    adminGetManufacturers: () => fetchWithAuth('/admin/manufacturers'),
    
    adminGetCategories: () => fetchWithAuth('/admin/categories'),

    // Areas
    getAreas: (projectId) => fetchWithAuth(`/projects/${projectId}/areas`),
    
    getArea: (projectId, areaId) => fetchWithAuth(`/projects/${projectId}/areas/${areaId}`),
    
    createArea: (projectId, data) => 
      fetchWithAuth(`/projects/${projectId}/areas`, { method: 'POST', body: JSON.stringify(data) }),
    
    updateArea: (projectId, areaId, data) => 
      fetchWithAuth(`/projects/${projectId}/areas/${areaId}`, { method: 'PUT', body: JSON.stringify(data) }),
    
    renameArea: (projectId, areaId, name) => 
      fetchWithAuth(`/projects/${projectId}/areas/${areaId}/rename`, { method: 'PATCH', body: JSON.stringify({ name }) }),
    
    cloneArea: (projectId, areaId, name) => 
      fetchWithAuth(`/projects/${projectId}/areas/${areaId}/clone`, { method: 'POST', body: JSON.stringify({ name }) }),
    
    deleteArea: (projectId, areaId) => 
      fetchWithAuth(`/projects/${projectId}/areas/${areaId}`, { method: 'DELETE' }),
  };
}

export default useApi;
